'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, LayoutGrid, Rows3, Map as MapIcon, X, Star, Check } from 'lucide-react';
import { cn } from '@/shadcn/lib/utils';
import { Listing } from '../types';
import { SearchListingCard } from './SearchListingCard';
import { ListingsMap } from './ListingsMap';
import { DemoMap } from './DemoMap';
import { HOME_SEARCH_EVENT, HomeSearchDetail } from './HomeSearchMode';
import { ListingDetailPanel, DetailToolbar } from './ListingDetailPanel';

/** DOCASNE: bez realneho MAPBOX_TOKEN je Mapbox biely -> staticka demo mapa. */
const USE_DEMO_MAP = false;

const EASE = '[transition-timing-function:cubic-bezier(.22,.61,.36,1)]';

type SortValue = 'relevance' | 'price_asc' | 'price_desc' | 'newest' | 'area_desc';
const SORT_OPTIONS: { value: SortValue; label: string }[] = [
    { value: 'relevance', label: 'Odporúčané' },
    { value: 'price_asc', label: 'Najlacnejšie' },
    { value: 'price_desc', label: 'Najdrahšie' },
    { value: 'newest', label: 'Najnovšie' },
    { value: 'area_desc', label: 'Najväčšia plocha' },
];

type View = 'grid' | 'list' | 'map';

type Props = {
    listings: Listing[];
};

/* ---------- mini parser dopytu (demo) ---------- */

const norm = (s: string) =>
    s
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

type Chip = { key: string; label: string };

type ParsedQuery = {
    category: 'APARTMENT' | 'HOUSE' | 'LAND' | null;
    txn: 'SALE' | 'RENT' | null;
    rooms: number | null;
    places: string[]; // normalizovane nazvy (municipality/district) z dat, ktore sedia na dopyt
    placeWords: string[]; // originalne slova (s diakritikou), ktore v dopyte sedeli - pre chip label
};

function parseQuery(query: string, listings: Listing[]): ParsedQuery {
    const q = norm(query);
    const category = /\b(byt|byty|bytu|bytov|izbov)/.test(q)
        ? 'APARTMENT'
        : /\b(dom|domy|domu|domov|rodinn|vila|chalup)/.test(q)
          ? 'HOUSE'
          : /\bpozem/.test(q)
            ? 'LAND'
            : null;
    const txn = /prenaj|najom|najat|prenajat/.test(q) ? 'RENT' : /predaj|kup|kupit/.test(q) ? 'SALE' : null;
    const rm = q.match(/(\d)\s*[- ]?\s*izb/);
    const rooms = rm ? Number(rm[1]) : null;

    // miesta: vezmi kazde slovo z municipality/district, ak sa vyskytuje v dopyte (min 4 znaky)
    const places = new Set<string>();
    const placeWords = new Set<string>();
    for (const l of listings) {
        for (const name of [l.municipality?.name, l.district?.name]) {
            if (!name) continue;
            const n = norm(name);
            const origWords = name.split(/[^\p{L}]+/u).filter((w) => w.length >= 4);
            const hit = origWords.filter((w) => q.includes(norm(w)));
            if (hit.length) {
                places.add(n);
                hit.forEach((w) => placeWords.add(w));
            }
        }
    }
    return { category, txn, rooms, places: [...places], placeWords: [...placeWords] };
}

const CATEGORY_LABEL = { APARTMENT: 'Byty', HOUSE: 'Domy', LAND: 'Pozemky' } as const;

/* ---------- komponent ---------- */

export function HomeSearchResults(props: Props) {
    const [query, setQuery] = useState('');
    const [sortBy, setSortBy] = useState<SortValue>('relevance');
    const [view, setView] = useState<View>('grid');
    const [activeListingId, setActiveListingId] = useState<string | null>(null);
    const [dropped, setDropped] = useState<Set<string>>(new Set());
    const [saved, setSaved] = useState(false);
    const toolbarRef = useRef<HTMLDivElement | null>(null);
    const [toolbarH, setToolbarH] = useState(0);
    const rootRef = useRef<HTMLDivElement | null>(null);

    /* ---------- detail v mieste (split view 2/3 + mapa 1/3) ---------- */
    const [detailId, setDetailId] = useState<string | null>(null);
    const [savedListings, setSavedListings] = useState<Set<string>>(new Set());
    // ci sme pushli vlastny history zaznam (Back ho zavrie); reload URL /inzerat/... ide na plnu stranku
    const pushedRef = useRef(false);

    const openDetail = (listing: Listing) => {
        const first = !detailId;
        setDetailId(listing.id);
        setActiveListingId(null);
        const url = `/inzerat/${listing.id}/${listing.slug}`;
        try {
            if (first) {
                window.history.pushState({ rxDetail: listing.id }, '', url);
                pushedRef.current = true;
            } else {
                window.history.replaceState({ rxDetail: listing.id }, '', url);
            }
        } catch {
            /* history nedostupna - detail bezi aj bez URL */
        }
        // ak je toolbar uz prilepeny pod listou (user bol hlboko v gride), vytiahni detail na jeho zaciatok;
        // ak je toolbar v zabere, nic - detail nahradi grid na mieste
        const root = rootRef.current;
        const tb = toolbarRef.current;
        if (root && tb) {
            const barH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--rx-bar-h')) || 0;
            const stuck = tb.getBoundingClientRect().top <= barH + 1 && window.scrollY > 0;
            if (stuck) {
                const top = root.getBoundingClientRect().top + window.scrollY - 72;
                window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
            }
        }
    };
    const closeDetail = () => {
        if (pushedRef.current) {
            // Back vrati URL na / a popstate zavrie detail
            pushedRef.current = false;
            window.history.back();
            return;
        }
        setDetailId(null);
    };

    // Back / Forward: stav detailu drzi history (URL /inzerat/... <-> /)
    useEffect(() => {
        const onPop = (e: PopStateEvent) => {
            const st = e.state as { rxDetail?: string } | null;
            let id: string | null = st?.rxDetail ?? null;
            if (!id) {
                const m = window.location.pathname.match(/^\/inzerat\/([^/]+)\//);
                id = m?.[1] ?? null;
            }
            const known = id && props.listings.some((l) => l.id === id) ? id : null;
            pushedRef.current = Boolean(known);
            setDetailId(known);
        };
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, [props.listings]);

    // Esc zavrie detail
    useEffect(() => {
        if (!detailId) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeDetail();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detailId]);

    // vyska toolbaru -> --rx-tb-h, aby sticky mapa sedela pod nim (lista + toolbar + 15px)
    useEffect(() => {
        const el = toolbarRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => setToolbarH(el.offsetHeight));
        ro.observe(el);
        setToolbarH(el.offsetHeight);
        return () => ro.disconnect();
    }, []);

    // dopyt zo smart inputu
    useEffect(() => {
        // debounce 250ms - kazdy znak by inak prefiltroval grid a prekreslil mapu
        let t: ReturnType<typeof setTimeout> | null = null;
        const onSearch = (e: Event) => {
            const d = (e as CustomEvent<HomeSearchDetail>).detail;
            if (!d?.active) return;
            if (t) clearTimeout(t);
            t = setTimeout(() => {
                setQuery(d.query ?? '');
                setDropped(new Set());
            }, 250);
        };
        window.addEventListener(HOME_SEARCH_EVENT, onSearch);
        return () => {
            if (t) clearTimeout(t);
            window.removeEventListener(HOME_SEARCH_EVENT, onSearch);
        };
    }, []);

    const parsed = useMemo(() => parseQuery(query, props.listings), [query, props.listings]);

    const chips: Chip[] = useMemo(() => {
        const out: Chip[] = [];
        if (parsed.txn) out.push({ key: 'txn', label: parsed.txn === 'RENT' ? 'Prenájom' : 'Predaj' });
        if (parsed.category) out.push({ key: 'category', label: CATEGORY_LABEL[parsed.category] });
        if (parsed.rooms) out.push({ key: 'rooms', label: `${parsed.rooms}-izbové` });
        if (parsed.placeWords.length) {
            const w = parsed.placeWords;
            out.push({ key: 'place', label: w.length > 2 ? `${w.slice(0, 2).join(', ')} +${w.length - 2}` : w.join(', ') });
        }
        return out.filter((c) => !dropped.has(c.key));
    }, [parsed, dropped]);

    const listings = useMemo(() => {
        const active = new Set(chips.map((c) => c.key));
        let list = props.listings.filter((l) => {
            if (active.has('category') && parsed.category && l.propertyCategory !== parsed.category) return false;
            if (active.has('rooms') && parsed.rooms) {
                const rc = (l.detail as { roomCount?: number } | null)?.roomCount;
                if (rc !== parsed.rooms) return false;
            }
            if (active.has('place') && parsed.places.length) {
                const names = [l.municipality?.name, l.district?.name].filter(Boolean).map((n) => norm(n as string));
                if (!names.some((n) => parsed.places.includes(n))) return false;
            }
            return true;
        });
        const areaOf = (l: Listing) => {
            const d = l.detail as { usableArea?: number; landArea?: number } | null;
            return d?.usableArea ?? d?.landArea ?? 0;
        };
        switch (sortBy) {
            case 'price_asc':
                list = [...list].sort((a, b) => (a.finalPrice ?? 0) - (b.finalPrice ?? 0));
                break;
            case 'price_desc':
                list = [...list].sort((a, b) => (b.finalPrice ?? 0) - (a.finalPrice ?? 0));
                break;
            case 'newest':
                list = [...list].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
                break;
            case 'area_desc':
                list = [...list].sort((a, b) => areaOf(b) - areaOf(a));
                break;
        }
        return list;
    }, [props.listings, chips, parsed, sortBy]);

    const count = listings.length;
    const countLabel = count === 1 ? '1 ponuka' : count >= 2 && count <= 4 ? `${count} ponuky` : `${count} ponúk`;
    // detail moze byt aj ponuka, ktoru aktualne chipy odfiltrovali - hladam v plnom zozname
    const detail = detailId ? props.listings.find((l) => l.id === detailId) ?? null : null;
    const placeLabel =
        chips.find((c) => c.key === 'place')?.label ?? 'Bratislava a okolie';

    return (
        <div
            ref={rootRef}
            className="pb-[40px]"
            style={{ ['--rx-tb-h' as string]: toolbarH + 'px' }}
        >
            {/* toolbar na plnu sirku (nad gridom aj mapou) - sticky pod zlozenou listou; miesto pre buduce smart filtre */}
            <div
                ref={toolbarRef}
                className={cn(
                    'rx-results-toolbar sticky z-[40] py-[12px]',
                    // page siva #F2F1ED (s priehladnostou pre blur)
                    'bg-[rgba(242,241,237,.9)] backdrop-blur-[12px]',
                    // top NEanimovat: --rx-bar-h uz bezi po frame-och s vyskou listy (ResizeObserver),
                    // vlastny transition by za nou zaostaval a robil dieru
                    'transition-[box-shadow,border-color] duration-[420ms]', EASE,
                    'border-b border-transparent',
                )}
                style={{ top: 'var(--rx-bar-h, 0px)' }}
            >
                <div className="cust-container !max-w-[100%] lg:!px-[20px]">
                {detail ? (
                    <div key="tb-detail" className="rx-home-in">
                        <DetailToolbar
                            listing={detail}
                            count={count}
                            saved={savedListings.has(detail.id)}
                            onToggleSaved={() =>
                                setSavedListings((s) => {
                                    const n = new Set(s);
                                    if (n.has(detail.id)) n.delete(detail.id);
                                    else n.add(detail.id);
                                    return n;
                                })
                            }
                            onClose={closeDetail}
                        />
                    </div>
                ) : (
                <div key="tb-results" className="rx-home-in">
                {/* jeden riadok: pocet + miesto + chipy | ulozit + sort + view */}
                <div className="flex items-center justify-between gap-[12px]">
                    <div className="flex min-w-0 flex-1 items-center gap-[10px] overflow-hidden">
                        <p className="shrink-0 text-[16px] font-[600] leading-none tracking-[-0.005em] xl:text-[17px]">
                            {countLabel}
                            <span className="font-[400] text-black/45"> · {placeLabel}</span>
                        </p>
                        {chips.length > 0 && (
                            <div className="flex items-center gap-[6px] overflow-hidden">
                                {chips.map((c) => (
                                    <button
                                        key={c.key}
                                        type="button"
                                        onClick={() => setDropped((s) => new Set(s).add(c.key))}
                                        className={cn(
                                            'group flex shrink-0 items-center gap-[6px] h-[30px] pl-[11px] pr-[7px] rounded-[15px]',
                                            'bg-[#F2F1ED] text-black/80 text-[12px] font-[500] leading-none',
                                            'transition duration-300', EASE,
                                            'hover:bg-black hover:text-[#F3C300]',
                                        )}
                                    >
                                        {c.label}
                                        <X className="size-[12px] opacity-50 transition duration-300 group-hover:opacity-100" strokeWidth={2} />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex shrink-0 items-center gap-[8px]">
                        <button
                            type="button"
                            onClick={() => setSaved((s) => !s)}
                            className={cn(
                                'flex shrink-0 items-center gap-[6px] h-[36px] px-[13px] rounded-[18px]',
                                'border text-[12px] font-[400] leading-none',
                                'transition duration-300', EASE,
                                saved
                                    ? 'border-[#F3C300] bg-[#F3C300] text-black'
                                    : 'border-black/10 bg-white text-black hover:-translate-y-[1px] hover:shadow-[0_8px_20px_-10px_rgba(0,0,0,0.28)]',
                            )}
                        >
                            <Star className={cn('size-[13px]', saved && 'fill-black')} strokeWidth={2} />
                            <span className="max-md:hidden">{saved ? 'Uložené' : 'Uložiť hľadanie'}</span>
                        </button>
                        <SortMenu value={sortBy} onChange={setSortBy} />
                        <ViewSwitch value={view} onChange={setView} />
                    </div>
                </div>
                </div>
                )}
                </div>
            </div>

            <div
                className={cn(
                    'grid items-start',
                    // sirka stlpcov sa animuje - rovnaky pocet trackov v oboch stavoch, fr <-> fr
                    'transition-[grid-template-columns] duration-[600ms]', EASE,
                    detail
                        ? 'lg:grid-cols-[2fr_1fr] 3xl:grid-cols-[2fr_1fr]'
                        : 'lg:grid-cols-[55fr_45fr] 3xl:grid-cols-[50fr_50fr]',
                )}
            >
                {/* ---------- lavy stlpec ---------- */}
                {/* pr = zlab medzi kartami a mapou; drzi sa na 20px rovnako ako lavy
                    okraj kariet a pravy okraj mapy - jedna hodnota na vsetkych sirkach */}
                <div className="cust-container !max-w-[100%] lg:!px-[20px]">
                    {detail && (
                        <div key={`detail-${detail.id}`} className="rx-home-in mt-[20px]">
                            <ListingDetailPanel
                                listing={detail}
                                others={listings.filter((l) => l.id !== detail.id)}
                                onClose={closeDetail}
                                onOpen={openDetail}
                            />
                        </div>
                    )}
                    {/* mobil: mapa ako view */}
                    {!detail && view !== 'map' && (
                        <div
                            key="results-grid"
                            className={cn(
                                'rx-home-in mt-[20px] grid items-stretch',
                                view === 'grid'
                                    ? 'gap-[16px] md:grid-cols-2 md:gap-[18px] 2xl:gap-[20px]'
                                    : 'gap-[12px]',
                            )}
                            onMouseLeave={() => setActiveListingId(null)}
                        >
                            {listings.map((listing) => (
                                <div
                                    key={listing.id}
                                    className="w-full h-full"
                                    onMouseEnter={() => setActiveListingId(listing.id)}
                                >
                                    <SearchListingCard
                                        listing={listing}
                                        layout={view === 'list' ? 'row' : 'grid'}
                                        active={activeListingId === listing.id}
                                        onOpen={openDetail}
                                    />
                                </div>
                            ))}
                            {count === 0 && (
                                <div className="md:col-span-2 rounded-[18px] border border-dashed border-black/15 py-[48px] text-center">
                                    <p className="text-[15px] font-[600]">Nič presne takéto tu zatiaľ nie&nbsp;je.</p>
                                    <p className="mt-[6px] text-[13px] text-black/55">
                                        Skús zrušiť niektorý filter, alebo si nechaj poslať novinky.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setDropped(new Set(['txn', 'category', 'rooms', 'place']))}
                                        className={cn('mt-[16px] h-[36px] px-[16px] rounded-[18px] bg-black text-[#F3C300] text-[12px] font-[700]', 'transition duration-300', EASE, 'hover:bg-[#F3C300] hover:text-black')}
                                    >
                                        Zobraziť všetky ponuky
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ---------- pravy stlpec: sticky mapa ---------- */}
                <div
                    className={cn(
                        'h-[420px] md:h-[520px]',
                        'lg:sticky lg:self-start',
                        // 20px vzduchu zhora aj sprava = rovnaky odstup, aky ma grid ponuk zlava
                        'lg:mt-[20px] lg:pr-[20px]',
                        'lg:top-[calc(var(--rx-bar-h,0px)+var(--rx-tb-h,0px)+20px)] lg:h-[calc(100vh-40px-var(--rx-bar-h,0px)-var(--rx-tb-h,0px))]',
                        (detail || view !== 'map') && 'max-lg:hidden',
                        'max-lg:mt-[18px] max-lg:px-[10px]',
                    )}
                >
                    <div className="h-full w-full overflow-hidden rounded-[18px] bg-[#ECEAE4]">
                        {USE_DEMO_MAP ? (
                            <DemoMap
                                listings={listings}
                                activeListingId={activeListingId}
                                onActiveChange={setActiveListingId}
                            />
                        ) : (
                            <ListingsMap
                                height="100%"
                                listings={listings}
                                activeListingId={activeListingId}
                                focusListingId={detailId}
                                center={[17.16, 48.2]}
                                zoom={9.5}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ---------- sort dropdown (custom, ziadny native select) ---------- */

function SortMenu(props: { value: SortValue; onChange: (v: SortValue) => void }) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (!ref.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);
    const current = SORT_OPTIONS.find((o) => o.value === props.value) ?? SORT_OPTIONS[0];
    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className={cn(
                    'flex items-center gap-[6px] h-[36px] pl-[13px] pr-[9px] rounded-[18px]',
                    'bg-white border border-black/10 text-[12px] font-[400] leading-none',
                    'transition duration-300', EASE,
                    'hover:-translate-y-[1px] hover:shadow-[0_8px_20px_-10px_rgba(0,0,0,0.28)]',
                    open && 'shadow-[0_8px_20px_-10px_rgba(0,0,0,0.28)] border-black/20',
                )}
            >
                <span className="text-black/45 font-[400]">Zoradiť</span>
                <span>{current.label}</span>
                <ChevronDown className={cn('size-[14px] transition duration-300', EASE, open && 'rotate-180')} />
            </button>
            <div
                className={cn(
                    // zarovnane s lavou hranou pillu; sirka = pill, rozsiri sa doprava len ak to obsah vyzaduje
                    'absolute left-0 top-[calc(100%+6px)] z-[60] min-w-full w-max origin-top-left',
                    'rounded-[14px] bg-white border border-black/10 p-[5px]',
                    'shadow-[0_18px_40px_-18px_rgba(0,0,0,0.3)]',
                    'transition duration-200', EASE,
                    open ? 'opacity-100 scale-100' : 'pointer-events-none opacity-0 scale-[0.97]',
                )}
            >
                {SORT_OPTIONS.map((o) => (
                    <button
                        key={o.value}
                        type="button"
                        onClick={() => {
                            props.onChange(o.value);
                            setOpen(false);
                        }}
                        className={cn(
                            'flex w-full items-center justify-between gap-[10px] h-[34px] px-[10px] rounded-[10px]',
                            'text-left text-[12px] font-[400]',
                            'transition duration-200', EASE,
                            'hover:bg-[#F2F1ED]',
                            o.value === props.value && 'font-[500]',
                        )}
                    >
                        {o.label}
                        {o.value === props.value && <Check className="size-[13px] text-[#F3C300]" strokeWidth={3} />}
                    </button>
                ))}
            </div>
        </div>
    );
}

/* ---------- prepinac zobrazenia (grid / list / mapa) ---------- */

function ViewSwitch(props: { value: View; onChange: (v: View) => void }) {
    const items: { value: View; icon: React.ReactNode; label: string; mobileOnly?: boolean }[] = [
        { value: 'grid', icon: <LayoutGrid className="size-[14px]" strokeWidth={1.75} />, label: 'Mriežka' },
        { value: 'list', icon: <Rows3 className="size-[14px]" strokeWidth={1.75} />, label: 'Zoznam' },
        { value: 'map', icon: <MapIcon className="size-[14px]" strokeWidth={1.75} />, label: 'Mapa', mobileOnly: true },
    ];
    const idx = Math.max(0, items.findIndex((i) => i.value === props.value));
    const SLOT = 34;
    const GAP = 2;
    return (
        <div className="relative flex h-[36px] items-center gap-[2px] rounded-[18px] border border-black/10 bg-white p-[3px]">
            {/* biely ramik so strokeom ako ostatne buttony, aktivny pill sivy #F2F1ED */}
            <span
                aria-hidden
                className="absolute left-[3px] top-[3px] h-[28px] w-[34px] rounded-[14px] bg-[#F2F1ED]"
                style={{
                    translate: `${idx * (SLOT + GAP)}px 0`,
                    transition: 'translate .42s cubic-bezier(.34,1.2,.55,1)',
                }}
            />
            {items.map((it) => (
                <button
                    key={it.value}
                    type="button"
                    aria-label={it.label}
                    title={it.label}
                    aria-pressed={props.value === it.value}
                    onClick={() => props.onChange(it.value)}
                    className={cn(
                        'relative z-[1] flex h-[28px] w-[34px] items-center justify-center rounded-[14px]',
                        'transition duration-300', EASE,
                        it.mobileOnly && 'lg:hidden',
                        props.value === it.value ? 'text-black' : 'text-black/45 hover:text-black',
                    )}
                >
                    {it.icon}
                </button>
            ))}
        </div>
    );
}
